import { useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { dismissStoryPrompt } from "../../redux/actions/storiesActions";
import { clearStoryPrompt } from "../../redux/reducer/storiesSlice";
import StoryComposer from "./StoryComposer";
import "./Stories.css";

/**
 * "Vuoi condividerlo nella tua storia?"
 *
 * Mounted once, at app level, and driven by the store — a proposal has to
 * outlive the screen that raised it, because saving a measure closes that
 * screen on the way out.
 *
 * Deliberately plain: the spec is explicit that the first prompt shows no
 * preview (§6). It arrives moments after the member finished something, so it
 * asks one question and gets out of the way.
 *
 * A portal rather than an IonModal, for the same reason the viewer is one: a
 * modal mounted with isOpen already true intermittently never presents, and a
 * prompt that silently fails to appear is a feature that silently does not exist.
 */
export default function StoryPromptSheet() {
  const dispatch = useDispatch();
  const story = useSelector((state) => state.stories.prompt);
  const [composing, setComposing] = useState(false);

  if (!story) return null;

  const close = () => {
    setComposing(false);
    dispatch(clearStoryPrompt());
  };

  const decline = () => {
    dispatch(
      dismissStoryPrompt({
        triggerType: story.trigger_type ?? story.type,
        sourceType: story.source_type,
        sourceId: story.source_id,
      }),
    );
    close();
  };

  const title = story.template?.title || "Vuoi condividerlo?";

  // ⚠️ "Provata" on a Cucina video only says the member COOKED it — not that
  // they liked it or would recommend it. So this trigger asks before borrowing
  // their name for a recommendation, and only a yes produces the "Provata e
  // consigliata" Storia. Every other trigger is already an achievement the
  // member chose to record, so the plain question stands.
  //
  // After the Ricette page moved its own button to recipe_rated, this trigger
  // reaches here only from a video — so there is one question, not a branch per
  // surface.
  const question =
    story.type === "recipe_tried"
      ? "L'hai provata e la consigli anche tu?"
      : "Vuoi condividerlo nella tua storia?";

  const sheet = (
    <div className="story-prompt-layer" role="dialog" aria-modal="true">
      <button
        type="button"
        className="story-prompt__backdrop"
        aria-label="Chiudi"
        onClick={close}
      />

      <div className="story-prompt">
        <span className="story-prompt__grabber" aria-hidden="true" />

        <h2 className="story-prompt__title">{title}</h2>
        <p className="story-prompt__question">{question}</p>

        <button
          type="button"
          className="story-prompt__primary"
          onClick={() => setComposing(true)}
        >
          Condividi nella storia
        </button>

        <button type="button" className="story-prompt__secondary" onClick={decline}>
          Non ora
        </button>
      </div>
    </div>
  );

  return (
    <>
      {!composing
        ? createPortal(sheet, document.querySelector("ion-app") ?? document.body)
        : null}

      {composing ? <StoryComposer prefill={story} onClose={close} /> : null}
    </>
  );
}
